import { EMPTY_CELL } from 'utils/empty-cell';
import { formatNumber } from 'utils/number';
import { ENTITY_TYPE_ITEMS } from 'lib/entities';
import type { EntitySearchParams } from './types';

/**
 * A count for display; a zero or missing count reads as empty.
 * @param {number | null | undefined} count The count.
 * @return {string} The formatted count.
 */
export const formatCount = (count: number | null | undefined): string => {
  if (!count) {
    return EMPTY_CELL;
  }
  return formatNumber(count, 0, 0, 'decimal', true, EMPTY_CELL);
};

/**
 * How many filters a search narrows from the list's defaults.
 * @param {EntitySearchParams | undefined} payload The search.
 * @return {number} The applied filter count.
 */
export function countAppliedFilters(payload: EntitySearchParams | undefined) {
  if (!payload) return 0;
  let count = 0;
  if (payload.createdAt?.start || payload.createdAt?.end) {
    count += 1;
  }
  if (payload.updatedAt?.start || payload.updatedAt?.end) {
    count += 1;
  }
  // Entity types default to all-checked; only a strict subset counts as a filter
  if (
    payload.entityTypes &&
    payload.entityTypes.length > 0 &&
    payload.entityTypes.length < ENTITY_TYPE_ITEMS.length
  ) {
    count += 1;
  }
  // Operator is unchecked by default; count it when checked
  if (payload.operatorOnly) {
    count += 1;
  }
  // Classification defaults to both checked; count only when narrowed to one
  const includePrimary = payload.includePrimary ?? true;
  const includeAliases = payload.includeAliases ?? true;
  if (!(includePrimary && includeAliases)) {
    count += 1;
  }
  // Deduplicated defaults to off; opting in counts as a filter
  if (payload.includeDeduplicated) {
    count += 1;
  }
  return count;
}
